import { format, startOfWeek } from 'date-fns';
import type { DayShiftEntry, EmployeeWeeklySchedule, Weekday } from '../modules/shift-management/types';
import { WEEK_STARTS_ON, WORK_WEEKDAYS } from '../modules/shift-management/schedulingConfig';
import {
  weekKeyFromDate,
  isDayOff,
  getWeekdayFromDate,
} from '../modules/shift-management/scheduleModel';
import {
  getPublishedAt as getPublishedAtForWeek,
  getPublishedEmployeeWeekSchedule,
} from '../modules/shift-management/shiftPublicationStorage';
import { displayClassName } from './groupFoundation';
import type { Class } from '../types/class';

export function getCurrentWeekKey(date = new Date()): string {
  return weekKeyFromDate(startOfWeek(date, { weekStartsOn: WEEK_STARTS_ON }));
}

type EmployeeIdKey = string | readonly string[];

/** Today's published assigned shift, or null if weekend / off / unpublished. */
export function getTodayAssignedShift(
  employeeId: EmployeeIdKey,
  date = new Date()
): DayShiftEntry | null {
  const weekday = getWeekdayFromDate(date);
  if (!weekday) return null;
  const schedule = getEmployeeWeekSchedule(employeeId, getCurrentWeekKey(date));
  const entry = schedule?.days[weekday] ?? null;
  if (!entry || isDayOff(entry)) return null;
  return entry;
}

/** True when punching out before the assigned published end time. */
export function isEarlyPunchOut(employeeId: EmployeeIdKey, at = new Date()): boolean {
  const entry = getTodayAssignedShift(employeeId, at);
  if (!entry?.end) return false;
  const nowHm = format(at, 'HH:mm');
  return nowHm < entry.end;
}

/** Employee & office portals only see manager-published (confirmed) shifts. */
export function getEmployeeWeekSchedule(
  employeeId: EmployeeIdKey,
  weekKey = getCurrentWeekKey()
): EmployeeWeeklySchedule | null {
  return getPublishedEmployeeWeekSchedule(weekKey, employeeId);
}

export function getPublishedAt(
  employeeId: EmployeeIdKey,
  weekKey = getCurrentWeekKey()
): string | null {
  return getPublishedAtForWeek(weekKey, employeeId);
}

export function resolveRoomName(roomId: string, classes: Class[] = []): string {
  const cls = classes.find((item) => item.id === roomId);
  if (cls) return displayClassName(cls);
  return roomId;
}

export interface DayScheduleSummary {
  day: Weekday;
  entry: DayShiftEntry | null;
  isOff: boolean;
  roomName: string | null;
}

export function summarizeWeekSchedule(
  schedule: EmployeeWeeklySchedule | null,
  classes: Class[] = []
): DayScheduleSummary[] {
  return WORK_WEEKDAYS.map((day) => {
    const entry = schedule?.days[day] ?? null;
    const off = isDayOff(entry);
    const roomId = entry?.roomIds[0];
    return {
      day,
      entry: off ? null : entry,
      isOff: off,
      roomName: roomId && !off ? resolveRoomName(roomId, classes) : null,
    };
  });
}

export function hasAssignedShifts(schedule: EmployeeWeeklySchedule | null): boolean {
  if (!schedule) return false;
  return WORK_WEEKDAYS.some((day) => {
    const entry = schedule.days[day];
    return entry && !isDayOff(entry);
  });
}
